'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import { addBookmark, editBookmark, removeBookmark } from '../apis/profile/bookmark';
import type { BookmarkAddFormType, BookmarkEditFormType, BookmarkItemType } from '../types/articles';

export const useAddBookmark = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation((args: BookmarkAddFormType) => addBookmark(args, user?.access_token as string), {
    onSettled: () => {
      queryClient.refetchQueries(['bookmarkList']);
    },
    onMutate: async (formData: BookmarkAddFormType) => {
      await queryClient.cancelQueries(['bookmarkList']);
      const prevBookmarks = queryClient.getQueryData<BookmarkItemType[]>(['bookmarkList']);

      if (prevBookmarks) {
        queryClient.setQueryData(['bookmarkList'], () => {
          return [...prevBookmarks, { ...formData, pending: true }];
        });
      }

      return { prevBookmarks };
    },
    onError: (_, variables, context) => {
      if (context?.prevBookmarks) {
        queryClient.setQueryData(
          ['bookmarkList'],
          context.prevBookmarks.filter((item) => item.name !== variables.name),
        );
      }
    },
  });
};

export const useEditBookmark = ({ onSuccess }: { onSuccess(): void }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation((args: BookmarkEditFormType) => editBookmark(args, user?.access_token as string), {
    onMutate: async (formData: BookmarkEditFormType) => {
      await queryClient.cancelQueries(['bookmarkList']);
      const prevBookmarks = queryClient.getQueryData<BookmarkItemType[]>(['bookmarkList']);

      queryClient.setQueryData(['bookmarkList'], () => {
        if (prevBookmarks) {
          return prevBookmarks.map((item: BookmarkItemType) => {
            if (item?.id === formData.id) {
              return { ...item, name: formData.name };
            }
            return item;
          });
        }
      });

      return prevBookmarks;
    },
    onSuccess,
    onSettled: () => {
      queryClient.refetchQueries(['bookmarkList']);
    },
    onError: (_err, _variables) => {
      queryClient.invalidateQueries(['bookmarkList']);
    },
  });
};

export const useRemoveBookmark = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation((args: string) => removeBookmark(args, user?.access_token as string), {
    onMutate: async (id: string) => {
      await queryClient.cancelQueries(['bookmarkList']);

      const prevBookmarks = queryClient.getQueryData<BookmarkItemType[]>(['bookmarkList']);

      queryClient.setQueryData(['bookmarkList'], () => {
        if (prevBookmarks) {
          return prevBookmarks.map((item) => {
            if (item.id === id) {
              return { ...item, pending: true };
            }
            return item;
          });
        }
      });

      return { prevBookmarks };
    },
    onSettled: () => {
      queryClient.invalidateQueries(['bookmarkList']);
    },
    onError: (_err, _variables, context) => {
      queryClient.setQueryData(['bookmarkList'], context?.prevBookmarks);
    },
  });
};
